import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  contentChild,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';

import { PopoverPanelDirective } from './popover-panel.directive';

/**
 * TopbarPopover — the shared anchor + panel shell for status-bar dropdowns
 * (weather, alerts bell). Owns the overlay behaviour every one of them was
 * hand-rolling: open state, click-outside dismissal, Escape, and the panel
 * chrome (right-aligned card, `shadow-overlay`, `z-[60]`), so the popovers
 * stay visually and behaviourally identical.
 *
 * The host is a fixed 40px-high anchor (the toolbar control height) that the
 * trigger centres inside — the panel offset is measured from this box, NOT
 * from the trigger, so panels open on the same line regardless of how tall
 * each trigger is (a 24px chip vs a 40px icon button). In the 64px topbar the
 * 12px offset lands the panel flush with the bar's bottom edge.
 *
 * The trigger is projected and stays the consumer's element — wire its click
 * to `popover.toggle()` and its aria-expanded to `popover.isOpen()` via the
 * export:
 *
 *   <app-topbar-popover #popover="appTopbarPopover" [width]="380" [ariaLabel]="t('…')">
 *     <button type="button" (click)="popover.toggle()"
 *             aria-haspopup="dialog" [attr.aria-expanded]="popover.isOpen()">…</button>
 *     <ng-template appPopoverPanel>…panel body…</ng-template>
 *   </app-topbar-popover>
 *
 * No @angular/cdk — same hand-rolled dismissal pattern as `app-select` and the
 * user-card overlay. The user-card and tenant-switcher are candidates to
 * migrate here too.
 */
@Component({
  selector: 'app-topbar-popover',
  exportAs: 'appTopbarPopover',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgTemplateOutlet],
  host: {
    class: 'relative inline-flex h-10 items-center',
    '(document:mousedown)': 'onDocumentMouseDown($event)',
    '(document:keydown.escape)': 'close()',
  },
  template: `
    <ng-content />

    @if (isOpen()) {
      <div
        role="dialog"
        [attr.aria-label]="ariaLabel() || null"
        class="absolute right-0 top-[calc(100%+12px)] z-[60] cursor-auto overflow-hidden rounded-card bg-card text-left font-body text-ink-1 shadow-overlay"
        [class.p-5]="padded()"
        [style.width.px]="width()"
      >
        <ng-container [ngTemplateOutlet]="panel().template" />
      </div>
    }
  `,
})
export class TopbarPopover {
  private readonly host = inject(ElementRef<HTMLElement>);

  /** Panel width in px. */
  readonly width = input(340);
  /** Standard p-5 body padding; off for panels that own their layout (lists). */
  readonly padded = input(true);
  readonly ariaLabel = input('');

  /** Fires on each open — e.g. to refresh the panel's data. */
  readonly opened = output<void>();

  protected readonly panel = contentChild.required(PopoverPanelDirective);

  private readonly open = signal(false);
  readonly isOpen = this.open.asReadonly();

  toggle(): void {
    if (this.open()) {
      this.close();
    } else {
      this.open.set(true);
      this.opened.emit();
    }
  }

  close(): void {
    this.open.set(false);
  }

  protected onDocumentMouseDown(event: MouseEvent): void {
    if (this.open() && !this.host.nativeElement.contains(event.target as Node)) {
      this.close();
    }
  }
}
